/**
 * Coalesces streamed `delta` text into one reducer update per frame (plan
 * §6.3), so a long answer costs one React render per frame, not one per
 * token. Framework-free (copied to next-mapit); the scheduler is injected.
 */

/**
 * Longest wait for a frame. `requestAnimationFrame` is paused in background
 * tabs, so the timer flushes there instead (browsers throttle it to ≥ 1 s,
 * which is plenty for a tab nobody is looking at).
 */
export const FRAME_FALLBACK_MS = 100;

/**
 * @typedef {Object} FrameScheduler
 * @property {(callback: () => void) => unknown} schedule Runs `callback` once, later.
 * @property {(handle: unknown) => void} cancel Cancels a pending `schedule`.
 */

/**
 * @typedef {Object} FrameSchedulerOptions
 * @property {((callback: () => void) => unknown) | null} [requestFrame]
 *   Defaults to `requestAnimationFrame` where it exists; null = timer only.
 * @property {(handle: unknown) => void} [cancelFrame] Defaults to `cancelAnimationFrame`.
 * @property {(callback: () => void, ms: number) => unknown} [setTimer] Defaults to `setTimeout`.
 * @property {(handle: unknown) => void} [clearTimer] Defaults to `clearTimeout`.
 * @property {number} [fallbackMs]
 */

/**
 * A scheduler that runs the callback on the next animation frame, or after
 * `fallbackMs` if no frame comes first, whichever is earlier — exactly once.
 *
 * @param {FrameSchedulerOptions} [options]
 * @returns {FrameScheduler}
 */
export function createFrameScheduler({
  requestFrame = typeof requestAnimationFrame === "function"
    ? (callback) => requestAnimationFrame(callback)
    : null,
  cancelFrame = (handle) => cancelAnimationFrame(handle),
  setTimer = (callback, ms) => setTimeout(callback, ms),
  clearTimer = (handle) => clearTimeout(handle),
  fallbackMs = FRAME_FALLBACK_MS,
} = {}) {
  const cancel = (handle) => {
    if (!handle || handle.settled) return;
    handle.settled = true;
    if (handle.frame !== null) cancelFrame(handle.frame);
    clearTimer(handle.timer);
  };

  const schedule = (callback) => {
    const handle = { frame: null, timer: null, settled: false };
    const run = () => {
      if (handle.settled) return;
      cancel(handle);
      callback();
    };
    if (requestFrame) handle.frame = requestFrame(run);
    handle.timer = setTimer(run, fallbackMs);
    return handle;
  };

  return { schedule, cancel };
}

/**
 * @typedef {Object} DeltaBatcher
 * @property {(text: string) => void} push Buffers text; schedules a flush if none is pending.
 * @property {() => void} flushNow Flushes synchronously (before `done` / `error` / Stop).
 * @property {() => void} dispose Drops the buffer and any pending flush; later pushes are ignored.
 */

/**
 * @param {{
 *   schedule: FrameScheduler["schedule"],
 *   cancel: FrameScheduler["cancel"],
 *   onFlush: (text: string) => void,
 * }} options `onFlush` gets the buffered text, never "".
 * @returns {DeltaBatcher}
 */
export function createDeltaBatcher({ schedule, cancel, onFlush }) {
  let buffer = "";
  let handle = null;
  // Identifies the pending flush, so a callback that fires after being
  // cancelled (a lax scheduler) can't flush twice or after `dispose`.
  let token = null;
  let disposed = false;

  const cancelPending = () => {
    if (token === null) return;
    token = null;
    cancel(handle);
    handle = null;
  };

  const flush = () => {
    if (disposed || !buffer) return;
    const text = buffer;
    buffer = "";
    onFlush(text);
  };

  return {
    push(text) {
      if (disposed || typeof text !== "string" || !text) return;
      buffer += text;
      if (token !== null) return;
      const own = {};
      token = own;
      handle = schedule(() => {
        if (token !== own) return;
        token = null;
        handle = null;
        flush();
      });
    },
    flushNow() {
      cancelPending();
      flush();
    },
    dispose() {
      cancelPending();
      disposed = true;
      buffer = "";
    },
  };
}
